/**
 * table-layout:fixed で % 指定が効かず余りが副次列へ流れるとき、
 * px の colgroup で主軸列へ配分する共通ヘルパー。
 */

export function remPx(rem: number): number {
  return Math.round(rem * 16);
}

/** thead の列数に揃えた widths（0 は非表示列用の空 col） */
export function setTableColgroupWidths(
  table: HTMLTableElement,
  widthsPx: number[]
): void {
  table.querySelectorAll('thead th').forEach((th) => {
    (th as HTMLElement).style.width = '';
  });
  table.querySelector('colgroup')?.remove();

  const cg = document.createElement('colgroup');
  widthsPx.forEach((w) => {
    const col = document.createElement('col');
    if (w > 0) col.style.width = `${w}px`;
    cg.appendChild(col);
  });
  table.insertBefore(cg, table.firstChild);
}

export function clearTableColgroupWidths(table: HTMLTableElement): void {
  table.querySelector('colgroup')?.remove();
  table.querySelectorAll('thead th').forEach((th) => {
    (th as HTMLElement).style.width = '';
  });
}

/** DataTables カードレイアウト（モバイル）では colgroup 不要（残すと grid 行が数十 px に潰れる） */
function isDataTableCardLayout(table: HTMLTableElement): boolean {
  return table.closest('.dataTables_wrapper')?.classList.contains('sample-dt-cards') === true
    || window.matchMedia('(max-width: 767.98px)').matches
      && Boolean(
        table.closest(
          '#sample-manager-table-wrapper, #sample-case-manager-page, #usage-records-page, #reports-table-wrapper, #storage-rooms-table-wrapper'
        )
      );
}

export type ColgroupRole = 'hidden' | 'fixed' | 'fluid';

/**
 * 可視列のうち fixed を差し引き、残りを fluid の weight 比で配分する。
 * 戻り値は thead th と同じ長さの px 配列。
 */
export function buildWeightedColgroupWidths(
  headers: Element[],
  resolve: (th: Element) => { role: ColgroupRole; widthPx?: number; weight?: number },
  totalPx: number,
  minFluid = 120
): number[] {
  let fixed = 0;
  let fluidWeight = 0;
  const specs = headers.map((th) => {
    if (getComputedStyle(th).display === 'none') {
      return { role: 'hidden' as const, widthPx: 0, weight: 0 };
    }
    const r = resolve(th);
    if (r.role === 'fixed') {
      const w = Math.max(0, r.widthPx ?? 0);
      fixed += w;
      return { role: 'fixed' as const, widthPx: w, weight: 0 };
    }
    if (r.role === 'fluid') {
      const weight = Math.max(0.01, r.weight ?? 1);
      fluidWeight += weight;
      return { role: 'fluid' as const, widthPx: 0, weight };
    }
    return { role: 'hidden' as const, widthPx: 0, weight: 0 };
  });

  const fluid = Math.max(minFluid, totalPx - fixed);
  const widths = specs.map((spec) => {
    if (spec.role === 'fixed') return spec.widthPx;
    if (spec.role === 'fluid') {
      return Math.floor((fluid * spec.weight) / fluidWeight);
    }
    return 0;
  });
  /** floor 余りを先頭 fluid へ戻し、枠幅との 1px ずれ（横スクロール）を防ぐ */
  const assigned = widths.reduce((sum, w) => sum + w, 0);
  const remainder = totalPx - assigned;
  if (remainder !== 0) {
    const fluidIdx = specs.findIndex((s) => s.role === 'fluid');
    if (fluidIdx >= 0) {
      widths[fluidIdx] = Math.max(0, widths[fluidIdx] + remainder);
    }
  }
  return widths;
}

/** 枠（wrapper）幅を正とする。table 自身は中身で膨らんでいることがある */
export function tableLayoutWidthPx(table: HTMLTableElement): number {
  const host = table.parentElement;
  const hostW = host ? host.getBoundingClientRect().width : 0;
  const tableW = table.getBoundingClientRect().width;
  return Math.max(0, Math.round(hostW > 0 ? hostW : tableW));
}

/**
 * colgroup 同期の共通前処理。
 * カードレイアウト／狭すぎる枠では null（呼び出し側は return）。
 * 成功時は配分に使う枠幅 px。
 */
export function prepareColgroupSync(table: HTMLTableElement | null): number | null {
  if (!table || typeof window === 'undefined') return null;
  if (isDataTableCardLayout(table)) {
    clearTableColgroupWidths(table);
    return null;
  }
  const total = tableLayoutWidthPx(table);
  return total < 200 ? null : total;
}

/** prepare → buildWeighted → set を一括 */
export function syncWeightedTableColgroup(
  table: HTMLTableElement | null,
  resolve: (th: Element) => { role: ColgroupRole; widthPx?: number; weight?: number },
  minFluid = 120
): void {
  const total = prepareColgroupSync(table);
  if (total == null || !table) return;
  const headers = [...table.querySelectorAll('thead th')];
  setTableColgroupWidths(
    table,
    buildWeightedColgroupWidths(headers, resolve, total, minFluid)
  );
}

/** 使用履歴日次表: 余りが編集/数量へ流れないよう名前へ配分（xs カード時は colgroup を外す） */
export function syncUsageDayTableColgroup(table: HTMLTableElement | null): void {
  const total = prepareColgroupSync(table);
  if (total == null || !table) return;
  const headers = [...table.querySelectorAll('thead th')];
  const widths = buildWeightedColgroupWidths(
    headers,
    (th) => {
      if (th.classList.contains('ur-time-col')) return { role: 'fixed', widthPx: remPx(4.5) };
      if (th.classList.contains('ur-edit-col')) return { role: 'fixed', widthPx: remPx(2.75) };
      if (th.classList.contains('ur-action-col')) return { role: 'fixed', widthPx: remPx(5) };
      if (th.classList.contains('ur-qty-col')) return { role: 'fixed', widthPx: remPx(3.75) };
      if (th.classList.contains('ur-remain-col')) return { role: 'fixed', widthPx: remPx(4.5) };
      if (th.classList.contains('ur-recorded-by-col')) return { role: 'fixed', widthPx: remPx(6) };
      if (th.classList.contains('ur-name-col')) return { role: 'fluid', weight: 1.2 };
      if (th.classList.contains('ur-location-col')) return { role: 'fluid', weight: 0.7 };
      if (th.classList.contains('ur-detail-col')) return { role: 'fluid', weight: 0.55 };
      return { role: 'fixed', widthPx: remPx(5) };
    },
    total
  );
  setTableColgroupWidths(table, widths);
}

/** ケース内試料表: 副次列非表示時も名前主軸（xs カード時は colgroup を外す） */
export function syncCaseSampleTableColgroup(table: HTMLTableElement | null): void {
  const total = prepareColgroupSync(table);
  if (total == null || !table) return;
  const headers = [...table.querySelectorAll('thead th')];
  const widths = buildWeightedColgroupWidths(
    headers,
    (th) => {
      if (th.classList.contains('case-sample-qty-col')) return { role: 'fixed', widthPx: remPx(4) };
      if (th.classList.contains('case-sample-bsl-col')) return { role: 'fixed', widthPx: remPx(4.5) };
      if (th.classList.contains('case-sample-status-col')) {
        return { role: 'fixed', widthPx: remPx(5.5) };
      }
      if (th.classList.contains('case-sample-type-col')) return { role: 'fixed', widthPx: remPx(7.5) };
      if (th.classList.contains('case-sample-owner-col')) return { role: 'fixed', widthPx: remPx(6) };
      if (th.classList.contains('case-sample-actions-col')) {
        /** 使用（短ラベル）+ 詳細/編集/削除アイコン。旧 2 ボタン幅だと overflow */
        return { role: 'fixed', widthPx: remPx(10) };
      }
      if (th.classList.contains('case-sample-name-col')) return { role: 'fluid', weight: 1 };
      return { role: 'fixed', widthPx: remPx(5) };
    },
    total
  );
  setTableColgroupWidths(table, widths);
}

/** サンプル一覧: カード幅いっぱいに配分。名前を主軸、保管は副次、操作は固定 */
export function syncSampleManagerTableColgroup(table: HTMLTableElement | null): void {
  const total = prepareColgroupSync(table);
  if (total == null || !table) return;
  /** xl+: 詳細/編集の短ラベル分を確保。md は使用ラベル+3アイコンが 9.5rem だと 1–2px 溢れる */
  const actionsRem = window.matchMedia('(min-width: 1200px)').matches ? 14 : 10.25;
  const headers = [...table.querySelectorAll('thead th')];
  const widths = buildWeightedColgroupWidths(
    headers,
    (th) => {
      if (th.classList.contains('sample-check-col')) {
        return { role: 'fixed', widthPx: remPx(2.5) };
      }
      if (th.classList.contains('sample-actions-col')) {
        return { role: 'fixed', widthPx: remPx(actionsRem) };
      }
      if (th.classList.contains('sample-creator-col')) {
        return { role: 'fixed', widthPx: remPx(7.5) };
      }
      if (th.classList.contains('sample-qty-col')) {
        return { role: 'fixed', widthPx: remPx(3.75) };
      }
      if (th.classList.contains('sample-bsl-col')) {
        return { role: 'fixed', widthPx: remPx(4.5) };
      }
      if (th.classList.contains('sample-type-cell')) {
        return { role: 'fixed', widthPx: remPx(6) };
      }
      if (th.classList.contains('sample-created-col')) {
        return { role: 'fixed', widthPx: remPx(5.5) };
      }
      if (th.classList.contains('sample-last-used-col')) {
        return { role: 'fixed', widthPx: remPx(8) };
      }
      if (th.classList.contains('sample-name-col')) {
        return { role: 'fluid', weight: 1.6 };
      }
      if (th.classList.contains('sample-storage-col')) {
        return { role: 'fluid', weight: 0.75 };
      }
      return { role: 'fixed', widthPx: remPx(5.5) };
    },
    total
  );
  setTableColgroupWidths(table, widths);
  table.style.removeProperty('width');
  table.style.removeProperty('max-width');
}
